import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Delete from '@material-ui/icons/Delete';
import PublishIcon from '@material-ui/icons/Publish';
import { useRollbar } from '@rollbar/react';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
// Material

// Components
import { CmsArticles, fetchCmsArticle } from '../../../shared/helpers/cms-articles';
import { uploadFile } from '../../../shared/helpers/upload-helpers';
import { EnumFileStatus, MAppSignature } from '../../redux/scholarship.models';

const useStyles = makeStyles(() =>
  createStyles({
    btnProgress: {
      color: green[500],
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -12,
      marginLeft: -12
    }
  })
);

const UploadButton = ({ appId, accept, dataSource, disabled, noClick, noDrag, noKeyboard, section, userId, handleSignature }) => {
  const classes = useStyles();
  const rollbar = useRollbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [compError, setCompError] = useState<boolean>(false);
  const [fileOpen, setFileOpen] = useState<boolean>(false);
  const [purchaserCertification, setPurchaserCertification] = useState('');
  const [purchaserLegalText, setPurchaserLegalText] = useState('');

  const { acceptedFiles, getRootProps, getInputProps, open } = useDropzone({
    accept,
    disabled,
    multiple: false,
    noClick,
    noDrag,
    noKeyboard
  });

  const handleDelete = (signature: MAppSignature) => {
    let currentSignature = signature;
    if (currentSignature) {
      currentSignature.signed = false;
    }
    if (Object.prototype.hasOwnProperty.call(currentSignature, 'filePath')) {
      delete currentSignature.filePath;
    }
    if (Object.prototype.hasOwnProperty.call(currentSignature, 'docStatus')) {
      delete currentSignature.docStatus;
    }
    handleSignature(appId, { ...currentSignature });
  };

  const doUpload = async () => {
    setFileOpen(false);
    for (const f of acceptedFiles) {
      setLoading(true);
      let newFileName = f.name.toString().replace(/[^0-9A-Za-z.\-_()]/gi, '');
      const extension = newFileName.split('.').pop();

      let filePath = '';
      if (dataSource.signatureId) {
        filePath = `mtpf/${userId}/${appId}/${section}/${section}_${dataSource.id}_${dataSource.signatureId}.${extension}`;
      } else {
        filePath = `mtpf/${userId}/${appId}/${section}/${newFileName}`;
      }

      try {
        const response = await uploadFile(f, filePath, section);
        if (response.status === 200) {
          handleSignature(appId, {
            ...dataSource,
            filePath: filePath.substring(5),
            docStatus: EnumFileStatus.PENDING,
            signed: true,
            signedAgreementWording: purchaserLegalText
          });
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        setCompError(true);
        const errorForLog = error instanceof Error ? error : new Error(String(error));

        rollbar.error('Upload failed in UploadButton', errorForLog, {
          appId,
          section,
          userId
        });
      }
    }
  };

  const handleToggle = (status: boolean) => {
    setFileOpen(status);
  };

  useEffect(() => {
    setCompError(false);
    void doUpload();
  }, [acceptedFiles]);

  useEffect(() => {
    setCompError(false);
  }, [dataSource]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const purchaserCertificationHTML = await fetchCmsArticle(CmsArticles.PURCHASER_CERTIFICATION);
        const purchaserLegalTextHTML = await fetchCmsArticle(CmsArticles.PURCHASER_LEGAL_TEXT);
        setPurchaserCertification(purchaserCertificationHTML);
        setPurchaserLegalText(purchaserLegalTextHTML);
        localStorage.setItem('purchaserCertification', purchaserCertification || '');
      } catch (e) {
        console.error(e);
      }
    };

    void fetchArticle();
  }, []);

  const renderButton = () => (
    <>
      <div {...getRootProps()}>
        <input {...getInputProps()} />
        <Button
          style={{ maxWidth: '60', maxHeight: '30px', minWidth: '60px', minHeight: '30px' }}
          variant="contained"
          color={`${compError ? 'secondary' : 'primary'}`}
          component="span"
          onClick={open}
          disabled={loading || !!dataSource.filePath}
        >
          <PublishIcon />
          {compError ? 'Error' : <>Upload {loading && <CircularProgress size={22} className={classes.btnProgress} />}</>}
        </Button>
        {dataSource.filePath && (
          <>
            <Dialog open={fileOpen} onClose={() => handleToggle(false)}>
              <DialogTitle id="delete-dialog-title">Delete File?</DialogTitle>
              <DialogContent>Are you sure you want to delete this file?</DialogContent>
              <DialogActions>
                <Button onClick={() => handleDelete(dataSource)} color="primary">
                  Yes
                </Button>
                <Button autoFocus onClick={() => handleToggle(false)} color="primary">
                  No
                </Button>
              </DialogActions>
            </Dialog>
            <IconButton size={'small'} onClick={() => handleToggle(true)}>
              <Delete />
            </IconButton>
          </>
        )}
      </div>
    </>
  );

  return <section>{renderButton()}</section>;
};

export default UploadButton;
